import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { timesheetAPI, tenantsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import {
  Clock, Plus, Calendar as CalendarIcon, CheckCircle, AlertCircle, User,
  FileText, RefreshCw, Edit, MoreHorizontal, Package, Download, Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TimesheetPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [cutoffs, setCutoffs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [packetJobs, setPacketJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [cutoffDialogOpen, setCutoffDialogOpen] = useState(false);
  const [packetDialogOpen, setPacketDialogOpen] = useState(false);
  const [packetGenerating, setPacketGenerating] = useState(false);
  const [filters, setFilters] = useState({ facility_id: '', needs_review: '' });
  const [entryForm, setEntryForm] = useState({ work_date: new Date(), time_in: '', time_out: '', facility_id: '' });
  const [cutoffForm, setCutoffForm] = useState({ name: '', period_start: new Date(), period_end: new Date(), facility_id: '' });
  const [packetForm, setPacketForm] = useState({ cutoff_id: '', facility_id: '', inclusion_mode: 'all' });
  const [packetSummary, setPacketSummary] = useState(null);

  const tenantId = user?.tenant_id || 'default';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tenant_id: tenantId, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      if (activeTab === 'entries') {
        const res = await timesheetAPI.listEntries(params);
        setEntries(res.data);
      } else if (activeTab === 'cutoffs') {
        const res = await timesheetAPI.listCutoffs(params);
        setCutoffs(res.data);
      } else if (activeTab === 'packets') {
        const res = await timesheetAPI.listPacketJobs({ tenant_id: tenantId });
        setPacketJobs(res.data);
      }
      const fRes = await tenantsAPI.listFacilities(tenantId);
      setFacilities(fRes.data);
    } catch (err) {
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateEntry = async () => {
    try {
      await timesheetAPI.createEntry({ tenant_id: tenantId, facility_id: entryForm.facility_id || 'default', user_id: user?.id, work_date: format(entryForm.work_date, 'yyyy-MM-dd'), time_in: entryForm.time_in, time_out: entryForm.time_out });
      toast.success('Entry created');
      setEntryDialogOpen(false);
      setEntryForm({ work_date: new Date(), time_in: '', time_out: '', facility_id: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create entry'); }
  };

  const handleCreateCutoff = async () => {
    if (!cutoffForm.name.trim()) { toast.error('Enter a cutoff name'); return; }
    try {
      await timesheetAPI.createCutoff({ tenant_id: tenantId, facility_id: cutoffForm.facility_id || null, name: cutoffForm.name, period_start: format(cutoffForm.period_start, 'yyyy-MM-dd'), period_end: format(cutoffForm.period_end, 'yyyy-MM-dd') });
      toast.success('Cutoff created');
      setCutoffDialogOpen(false);
      setCutoffForm({ name: '', period_start: new Date(), period_end: new Date(), facility_id: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create cutoff'); }
  };

  const handleProcessCutoff = async (id) => {
    try {
      const res = await timesheetAPI.processCutoff(id);
      toast.success(`Cutoff processed: ${res.data.summary.total_hours} hours`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to process cutoff'); }
  };

  const handleGeneratePacket = async () => {
    if (!packetForm.cutoff_id) { toast.error('Select a cutoff'); return; }
    setPacketGenerating(true);
    setPacketSummary(null);
    try {
      const res = await timesheetAPI.generatePacket(packetForm.cutoff_id, packetForm.facility_id || undefined, packetForm.inclusion_mode);
      toast.success('Payroll packet generation started');
      // Poll for job status
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const jobRes = await timesheetAPI.listPacketJobs({ tenant_id: tenantId });
          const job = jobRes.data.find(j => j.id === jobId);
          if (job && (job.status === 'completed' || job.status === 'failed')) {
            clearInterval(poll);
            setPacketGenerating(false);
            if (job.status === 'completed') {
              setPacketSummary(job.summary);
              toast.success('Payroll packet generated');
            } else {
              toast.error(job.error_message || 'Packet generation failed');
            }
            loadData();
          }
        } catch { clearInterval(poll); setPacketGenerating(false); }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start generation');
      setPacketGenerating(false);
    }
  };

  const handleVerifyEntry = async (id) => {
    try { await timesheetAPI.verifyEntry(id); toast.success('Entry verified'); loadData(); }
    catch { toast.error('Failed to verify entry'); }
  };

  const calcHours = (tIn, tOut) => {
    if (!tIn || !tOut) return '-';
    try { const [iH, iM] = tIn.split(':').map(Number); const [oH, oM] = tOut.split(':').map(Number); return ((oH * 60 + oM - iH * 60 - iM) / 60).toFixed(1); }
    catch { return '-'; }
  };

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Timesheets</h1>
          <p className="text-slate-500 mt-1">Manage time entries, payroll cutoffs, and generate packets</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="entries" data-testid="tab-entries"><Clock className="mr-2 h-4 w-4" />Entries</TabsTrigger>
            <TabsTrigger value="cutoffs" data-testid="tab-cutoffs"><FileText className="mr-2 h-4 w-4" />Cutoffs</TabsTrigger>
            <TabsTrigger value="packets" data-testid="tab-packets"><Package className="mr-2 h-4 w-4" />Packets</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            {activeTab === 'entries' && (
              <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
                <DialogTrigger asChild><Button data-testid="add-entry-btn"><Plus className="mr-2 h-4 w-4" />Add Entry</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Time Entry</DialogTitle><DialogDescription>Create a new timesheet entry</DialogDescription></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2"><Label>Work Date</Label>
                      <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(entryForm.work_date, 'PPP')}</Button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={entryForm.work_date} onSelect={(d) => d && setEntryForm(p => ({...p, work_date: d}))} /></PopoverContent></Popover></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Time In</Label><Input type="time" value={entryForm.time_in} onChange={e => setEntryForm(p => ({...p, time_in: e.target.value}))} data-testid="time-in-input" /></div>
                      <div className="space-y-2"><Label>Time Out</Label><Input type="time" value={entryForm.time_out} onChange={e => setEntryForm(p => ({...p, time_out: e.target.value}))} data-testid="time-out-input" /></div>
                    </div>
                    {facilities.length > 0 && (<div className="space-y-2"><Label>Facility</Label><Select value={entryForm.facility_id} onValueChange={v => setEntryForm(p => ({...p, facility_id: v}))}><SelectTrigger><SelectValue placeholder="Select facility" /></SelectTrigger><SelectContent>{facilities.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select></div>)}
                    <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setEntryDialogOpen(false)}>Cancel</Button><Button onClick={handleCreateEntry} data-testid="save-entry-btn">Save</Button></div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {activeTab === 'cutoffs' && (
              <>
                <Dialog open={cutoffDialogOpen} onOpenChange={setCutoffDialogOpen}>
                  <DialogTrigger asChild><Button data-testid="add-cutoff-btn"><Plus className="mr-2 h-4 w-4" />New Cutoff</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Create Payroll Cutoff</DialogTitle><DialogDescription>Define a new payroll period</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2"><Label>Name</Label><Input placeholder="e.g., March 2026 - Week 1" value={cutoffForm.name} onChange={e => setCutoffForm(p => ({...p, name: e.target.value}))} data-testid="cutoff-name-input" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>Start</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(cutoffForm.period_start, 'PP')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={cutoffForm.period_start} onSelect={d => d && setCutoffForm(p => ({...p, period_start: d}))} /></PopoverContent></Popover></div>
                        <div className="space-y-2"><Label>End</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(cutoffForm.period_end, 'PP')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={cutoffForm.period_end} onSelect={d => d && setCutoffForm(p => ({...p, period_end: d}))} /></PopoverContent></Popover></div>
                      </div>
                      <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setCutoffDialogOpen(false)}>Cancel</Button><Button onClick={handleCreateCutoff} data-testid="save-cutoff-btn">Create</Button></div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={packetDialogOpen} onOpenChange={v => { setPacketDialogOpen(v); if (!v) setPacketSummary(null); }}>
                  <DialogTrigger asChild><Button variant="outline" data-testid="generate-packet-btn"><Package className="mr-2 h-4 w-4" />Generate Packet</Button></DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader><DialogTitle>Generate Payroll Timesheet Packet</DialogTitle><DialogDescription>Merge all timesheet files into a single PDF</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2"><Label>Payroll Cutoff *</Label><Select value={packetForm.cutoff_id} onValueChange={v => setPacketForm(p => ({...p, cutoff_id: v}))}><SelectTrigger data-testid="packet-cutoff-select"><SelectValue placeholder="Select cutoff" /></SelectTrigger><SelectContent>{cutoffs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
                      {facilities.length > 0 && (<div className="space-y-2"><Label>Facility (optional)</Label><Select value={packetForm.facility_id} onValueChange={v => setPacketForm(p => ({...p, facility_id: v}))}><SelectTrigger><SelectValue placeholder="All facilities" /></SelectTrigger><SelectContent><SelectItem value="all_facilities">All Facilities</SelectItem>{facilities.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select></div>)}
                      <div className="space-y-2"><Label>Inclusion Mode</Label><Select value={packetForm.inclusion_mode} onValueChange={v => setPacketForm(p => ({...p, inclusion_mode: v}))}><SelectTrigger data-testid="packet-mode-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Include All</SelectItem><SelectItem value="approved_only">Approved Only</SelectItem><SelectItem value="exclude_pending_ocr">Exclude Pending OCR</SelectItem><SelectItem value="exclude_needs_review">Exclude Needs Review</SelectItem></SelectContent></Select></div>

                      {packetSummary && (
                        <Card className="bg-slate-50 border-slate-200">
                          <CardContent className="p-4 space-y-2">
                            <h4 className="font-medium text-slate-900">Generation Summary</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>Users Included: <span className="font-mono font-medium">{packetSummary.total_users}</span></div>
                              <div>Documents Merged: <span className="font-mono font-medium">{packetSummary.total_documents}</span></div>
                              <div>Files Skipped: <span className="font-mono font-medium">{packetSummary.total_skipped}</span></div>
                              <div>Pending Review: <span className="font-mono font-medium text-amber-600">{packetSummary.pending_review}</span></div>
                              <div>Pending OCR: <span className="font-mono font-medium text-blue-600">{packetSummary.pending_ocr}</span></div>
                              <div>Corrupted: <span className="font-mono font-medium text-red-600">{packetSummary.corrupted}</span></div>
                              {packetSummary.duplicates_suspected > 0 && <div className="col-span-2 text-amber-600">Duplicate Suspects: {packetSummary.duplicates_suspected}</div>}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setPacketDialogOpen(false)}>Close</Button>
                        {!packetSummary && (
                          <Button onClick={handleGeneratePacket} disabled={packetGenerating} data-testid="start-generation-btn">
                            {packetGenerating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : <><Package className="mr-2 h-4 w-4" />Generate</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Entries Tab */}
        <TabsContent value="entries" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div><CardTitle>Time Entries</CardTitle><CardDescription>All timesheet entries</CardDescription></div>
                <div className="flex items-center gap-2">
                  <Select value={filters.needs_review || "all"} onValueChange={v => setFilters(p => ({...p, needs_review: v === "all" ? "" : v}))}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-review"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="true">Needs Review</SelectItem><SelectItem value="false">Verified</SelectItem></SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6 space-y-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
              : entries.length === 0 ? <div className="text-center py-16"><Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-900">No entries</h3></div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {entries.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium">{e.work_date}</TableCell>
                        <TableCell><div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{e.user_name || 'Unknown'}</div></TableCell>
                        <TableCell className="font-mono">{e.time_in || '-'}</TableCell>
                        <TableCell className="font-mono">{e.time_out || '-'}</TableCell>
                        <TableCell className="font-mono">{e.hours_worked?.toFixed(1) || calcHours(e.time_in, e.time_out)}</TableCell>
                        <TableCell>
                          {e.is_verified ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="mr-1 h-3 w-3" />Verified</Badge>
                          : e.needs_review ? <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="mr-1 h-3 w-3" />Review</Badge>
                          : <Badge variant="secondary">Pending</Badge>}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">{!e.is_verified && <DropdownMenuItem onClick={() => handleVerifyEntry(e.id)}><CheckCircle className="mr-2 h-4 w-4" />Verify</DropdownMenuItem>}<DropdownMenuItem><Edit className="mr-2 h-4 w-4" />Edit</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cutoffs Tab */}
        <TabsContent value="cutoffs" className="mt-6">
          <Card>
            <CardHeader><CardTitle>Payroll Cutoffs</CardTitle><CardDescription>Manage payroll periods and generate packets</CardDescription></CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
              : cutoffs.length === 0 ? <div className="text-center py-16"><FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-900">No cutoffs</h3></div>
              : (
                <div className="divide-y">
                  {cutoffs.map(c => (
                    <div key={c.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div><h4 className="font-medium text-slate-900">{c.name}</h4><p className="text-sm text-slate-500 mt-1">{c.period_start} to {c.period_end}</p></div>
                        <div className="flex items-center gap-3">
                          {c.is_processed ? (<div className="text-right"><Badge className="bg-emerald-100 text-emerald-700">Processed</Badge><p className="text-sm text-slate-500 mt-1">{c.total_hours?.toFixed(1)} hrs / {c.total_employees} employees</p></div>)
                          : (<Button onClick={() => handleProcessCutoff(c.id)} data-testid={`process-cutoff-${c.id}`}>Process</Button>)}
                          {c.merged_pdf_path && <Button variant="outline" size="icon"><Download className="h-4 w-4" /></Button>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packets Tab */}
        <TabsContent value="packets" className="mt-6">
          <Card>
            <CardHeader><div className="flex justify-between items-center"><div><CardTitle>Generated Packets</CardTitle><CardDescription>Payroll PDF packet jobs</CardDescription></div><Button variant="outline" size="icon" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button></div></CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
              : packetJobs.length === 0 ? <div className="text-center py-16"><Package className="h-12 w-12 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-900">No packets generated</h3><p className="text-slate-500 mt-1">Generate your first payroll packet from the Cutoffs tab</p></div>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>Cutoff</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead>Summary</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {packetJobs.map(j => (
                      <TableRow key={j.id}>
                        <TableCell className="font-mono text-xs">{j.id.slice(0, 20)}...</TableCell>
                        <TableCell>{j.cutoff_id?.slice(0, 12)}...</TableCell>
                        <TableCell className="capitalize">{j.inclusion_mode}</TableCell>
                        <TableCell><Badge className={j.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : j.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>{j.status}</Badge></TableCell>
                        <TableCell className="text-sm">{j.summary ? `${j.summary.total_users} users, ${j.summary.total_documents} docs` : '-'}</TableCell>
                        <TableCell>{j.file_path && <Button variant="ghost" size="sm"><Download className="h-4 w-4" /></Button>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TimesheetPage;
